/**
 * MUTAGIM.SHOP — Admin Help Page (Phase 20)
 * Documentation et guides pour les administrateurs
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft, Search, ChevronDown, ChevronRight,
  Package, CreditCard, Users, Settings, Image, 
  Mail, Shield, BarChart3, Zap, HelpCircle,
  BookOpen, Video, MessageCircle, ExternalLink,
  Sun, Moon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AdminSidebar, AdminSidebarToggle } from '@/components/admin/AdminSidebar';
import { useAdminTheme } from '@/contexts/AdminThemeContext';

// Guides organisés par catégorie
const HELP_GUIDES = [
  {
    category: "Premiers Pas",
    icon: BookOpen,
    color: "bg-blue-500",
    guides: [
      {
        id: "onboarding",
        title: "Wizard Onboarding",
        description: "Configurez votre boutique en 8 étapes simples",
        content: `
## Wizard Onboarding

Le wizard vous guide à travers 8 étapes essentielles :

1. **Informations boutique** — Nom, logo et slogan
2. **Langues & Devises** — Activez FR/EN/HE et configurez les devises
3. **Connexion Agent** — Configurez vos credentials ACBuy/KakoBuy
4. **Paiements** — Stripe, PayPal, Bit
5. **Emails** — Resend API pour les emails transactionnels
6. **Catégories** — Créez vos premières catégories
7. **Import Produit** — Importez votre premier produit
8. **SEO** — Configurez les meta titles/descriptions

💡 **Astuce** : Vous pouvez ignorer une étape et y revenir plus tard.
        `
      },
      {
        id: "first-product",
        title: "Importer votre premier produit",
        description: "Comment ajouter un produit depuis Taobao ou Weidian",
        content: `
## Importer un Produit

### Via URL (Recommandé)
1. Allez dans **Sourcing > Import**
2. Collez l'URL du produit (Taobao, Weidian, 1688, ou via agent)
3. Activez les options :
   - ✅ Générer nom IA
   - ✅ Générer description IA
4. Sélectionnez une catégorie
5. Cliquez sur **Importer**

### Via fichier CSV
1. Préparez un fichier CSV avec une colonne \`url\`
2. Allez dans **Sourcing > Import CSV**
3. Uploadez votre fichier
4. Les produits seront traités en arrière-plan

⚠️ **Note** : Sans clé OTCommerce, les produits sont importés avec des données de base. L'enrichissement IA utilise Claude.
        `
      }
    ]
  },
  {
    category: "Commandes",
    icon: Package,
    color: "bg-green-500",
    guides: [
      {
        id: "order-flow",
        title: "Cycle de vie d'une commande",
        description: "Comprendre les différentes étapes d'une commande",
        content: `
## Cycle de Vie d'une Commande

### États de la commande
1. **Pending** — Commande créée, en attente de paiement
2. **Paid** — Paiement reçu
3. **Processing** — Commande passée chez l'agent
4. **QC Ready** — Photos qualité disponibles
5. **Shipped** — Expédié, tracking disponible
6. **Delivered** — Livré au client

### Actions Admin
- **Voir QC** : Examinez les photos de contrôle qualité
- **Approuver QC** : Autorise l'expédition
- **Ajouter Tracking** : Saisissez manuellement le numéro de suivi
- **Annuler** : Annule la commande (avant expédition uniquement)

### Commandes Multi-Agents
Si une commande contient des produits de plusieurs agents, elle est automatiquement splitée en sous-commandes. Chaque sous-commande a son propre tracking.
        `
      },
      {
        id: "qc-photos",
        title: "Photos de Contrôle Qualité (QC)",
        description: "Comment gérer les photos QC de vos commandes",
        content: `
## Photos QC

### Qu'est-ce que le QC ?
Les photos QC sont prises par l'agent à l'arrivée du produit dans leur entrepôt. Elles permettent de vérifier la qualité avant expédition.

### Processus
1. Le système récupère automatiquement les photos QC (toutes les 2h)
2. Une notification vous alerte quand les photos sont disponibles
3. Examinez les photos dans **Admin > Commandes > [Commande]**
4. Cliquez sur **Approuver QC** ou **Rejeter**

### Si vous rejetez
- L'agent sera contacté pour un échange ou remboursement
- Expliquez clairement le problème (défaut, mauvaise couleur, etc.)
        `
      }
    ]
  },
  {
    category: "Paiements",
    icon: CreditCard,
    color: "bg-purple-500",
    guides: [
      {
        id: "stripe-setup",
        title: "Configurer Stripe",
        description: "Activez les paiements par carte bancaire",
        content: `
## Configuration Stripe

### Obtenir vos clés
1. Créez un compte sur [stripe.com](https://stripe.com)
2. Allez dans **Developers > API Keys**
3. Copiez votre **Secret Key** (commence par \`sk_live_\`)

### Configuration dans Mutagim
1. Allez dans **Admin > Paramètres > Paiements**
2. Collez votre clé dans le champ Stripe
3. Sauvegardez

### Apple Pay & Google Pay
Ces options sont automatiquement activées via Stripe. Assurez-vous de configurer votre domaine dans le Dashboard Stripe.

⚠️ **Production** : Utilisez uniquement les clés \`sk_live_\` en production. Les clés \`sk_test_\` sont pour les tests uniquement.
        `
      },
      {
        id: "refunds",
        title: "Gérer les remboursements",
        description: "Procédure de remboursement client",
        content: `
## Remboursements

### Via Stripe Dashboard
1. Connectez-vous à [dashboard.stripe.com](https://dashboard.stripe.com)
2. Trouvez le paiement dans **Payments**
3. Cliquez sur **Refund**

### Remboursement partiel
Vous pouvez rembourser une partie du montant (ex: si un article sur deux est défectueux).

### Chargebacks
En cas de chargeback :
1. Vous recevrez une notification Stripe
2. Fournissez les preuves (tracking, photos QC, emails)
3. Le système stocke automatiquement l'IP et le fingerprint pour vous défendre
        `
      }
    ]
  },
  {
    category: "Mutagim Crew",
    icon: Users,
    color: "bg-orange-500",
    guides: [
      {
        id: "crew-program",
        title: "Programme d'affiliation Crew",
        description: "Gérez votre réseau d'affiliés",
        content: `
## Mutagim Crew

### Concept
Le double 10% : le client économise 10% avec un code, le membre Crew gagne 10% de commission.

### Niveaux
- **Crew** (0-5 ventes) : 10%
- **Crew OG** (6-20 ventes) : 12%
- **Crew Elite** (21-50 ventes) : 15%
- **Crew Legend** (50+) : 18%

### Gestion
1. Allez dans **Admin > Crew**
2. Approuvez/rejetez les demandes
3. Visualisez les performances
4. Payez les commissions dues

### Attribution
- Le cookie de tracking dure 30 jours
- Priorité : code promo > cookie
        `
      }
    ]
  },
  {
    category: "Automatisation",
    icon: Zap,
    color: "bg-yellow-500",
    guides: [
      {
        id: "playwright-mode",
        title: "Mode Simulation vs Production",
        description: "Comprendre les modes d'automatisation",
        content: `
## Modes d'Automatisation

### Mode SIMULATION (défaut)
- Aucune action réelle n'est effectuée
- Les logs montrent ce qui SERAIT fait
- Idéal pour tester la configuration

### Mode PRODUCTION
- Les actions sont réellement exécutées
- Commandes passées sur les agents
- Photos QC récupérées automatiquement

### Changer de mode
1. Allez dans **Admin > Automatisation**
2. Cliquez sur le switch SIMULATION/PRODUCTION
3. En production, assurez-vous d'avoir :
   - Credentials agent valides
   - Solde agent suffisant
   - Clé 17track configurée

⚠️ **Attention** : En mode PRODUCTION, les commandes sont réellement passées et facturées sur votre compte agent.
        `
      },
      {
        id: "agent-balance",
        title: "Surveiller les soldes agents",
        description: "Évitez les commandes bloquées",
        content: `
## Soldes Agents

### Alertes automatiques
- **Warning** : Solde < $200 → Badge orange
- **Critique** : Solde < $50 → Badge rouge + notification

### Configuration
1. Allez dans **Admin > Automatisation > Onglet Soldes**
2. Ajustez les seuils d'alerte par agent

### Recharger un compte
Connectez-vous directement sur le site de l'agent (ACBuy, KakoBuy...) pour recharger votre compte.
        `
      }
    ]
  },
  {
    category: "Sécurité",
    icon: Shield,
    color: "bg-red-500",
    guides: [
      {
        id: "fraud-detection",
        title: "Détection de fraude",
        description: "Comprendre le système anti-fraude",
        content: `
## Système Anti-Fraude

### Stripe Radar
- Score de risque calculé automatiquement
- > 75 : Commande bloquée automatiquement
- 50-75 : Validation manuelle requise

### Blacklist
Vous pouvez bloquer :
- Emails
- Adresses IP
- Numéros de carte (6 premiers chiffres)

### Données collectées
Pour chaque commande, nous stockons :
- Adresse IP
- User-Agent du navigateur
- Fingerprint de session

Ces données sont utilisées uniquement pour la protection contre les chargebacks.
        `
      }
    ]
  },
  {
    category: "Analytics",
    icon: BarChart3,
    color: "bg-cyan-500",
    guides: [
      {
        id: "dashboard-metrics",
        title: "Comprendre les métriques",
        description: "Guide des KPIs du dashboard",
        content: `
## Métriques Principales

### Chiffre d'Affaires (CA)
Total des ventes sur la période sélectionnée.

### Panier Moyen
CA divisé par le nombre de commandes.

### Taux de Conversion
Visiteurs qui ont finalisé un achat / Total visiteurs.

### Taux d'Abandon
Paniers créés mais non finalisés.

### Export PDF
Cliquez sur "Export PDF" pour télécharger un rapport complet avec graphiques.
        `
      }
    ]
  }
];

export function AdminHelpPage() {
  const navigate = useNavigate();
  const { isLight, toggleTheme } = useAdminTheme();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedGuide, setSelectedGuide] = useState(null);

  // Filtrer les guides selon la recherche
  const filteredGuides = searchTerm
    ? HELP_GUIDES.map(cat => ({
        ...cat,
        guides: cat.guides.filter(g =>
          g.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          g.description.toLowerCase().includes(searchTerm.toLowerCase())
        )
      })).filter(cat => cat.guides.length > 0)
    : HELP_GUIDES;

  const toggleCategory = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
    setSelectedGuide(null);
  };

  const selectGuide = (guide) => {
    setSelectedGuide(selectedGuide?.id === guide.id ? null : guide);
  };

  return (
    <div className={cn(
      "min-h-screen transition-colors duration-200",
      isLight ? "bg-gray-100 admin-light-theme" : "bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950"
    )} data-testid="admin-help-page">
      {/* Sidebar */}
      <AdminSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(false)} />
      
      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : ''}`}>
        {/* Header */}
        <header className={cn(
          "border-b backdrop-blur-sm sticky top-0 z-40",
          isLight ? "border-gray-200 bg-white/90" : "border-zinc-800 bg-zinc-900/50"
        )}>
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <AdminSidebarToggle onClick={() => setSidebarOpen(true)} />
              <div>
                <h1 className={cn("text-xl font-bold flex items-center gap-2", isLight ? "text-gray-900" : "text-white")}>
                  <HelpCircle className={cn("h-6 w-6", isLight ? "text-blue-600" : "text-mutagim-red")} />
                  Centre d'Aide Admin
                </h1>
                <p className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-400")}>
                  Guides et documentation
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <a
                href="mailto:support@mutagim.shop"
                className={cn("text-sm flex items-center gap-1", isLight ? "text-gray-600 hover:text-blue-600" : "text-gray-400 hover:text-mutagim-red")}
              >
                <MessageCircle className="h-4 w-4" />
                Contacter le support
              </a>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={toggleTheme}
                className={cn(
                  "gap-2 transition-colors",
                  isLight 
                    ? "border-gray-300 bg-white hover:bg-gray-100 text-gray-700" 
                    : "border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-gray-300"
                )}
              >
                {isLight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                <span className="hidden sm:inline">{isLight ? 'Sombre' : 'Clair'}</span>
              </Button>
            </div>
          </div>
        </header>

      <main className="p-4 md:p-6">
        {/* Search */}
        <div className="mb-8">
          <div className="relative max-w-xl mx-auto">
            <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5", isLight ? "text-gray-400" : "text-gray-500")} />
            <Input
              type="text"
              placeholder="Rechercher dans l'aide..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 py-6 text-lg"
              data-testid="help-search"
            />
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Checklist Production", href: "/admin/settings/production-checklist", icon: Settings },
            { label: "Automatisation", href: "/admin/automation", icon: Zap },
            { label: "Analytics", href: "/admin/analytics", icon: BarChart3 },
            { label: "Crew", href: "/admin/crew", icon: Users }
          ].map(link => (
            <Card
              key={link.href}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(link.href)}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <link.icon className="h-5 w-5 text-mutagim-red" />
                <span className="text-sm font-medium">{link.label}</span>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Categories Accordion */}
        <div className="space-y-4">
          {filteredGuides.map(category => {
            const CategoryIcon = category.icon;
            const isExpanded = expandedCategory === category.category;

            return (
              <Card key={category.category}>
                <CardHeader
                  className="cursor-pointer"
                  onClick={() => toggleCategory(category.category)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-3">
                      <div className={cn("p-2 rounded-lg", category.color)}>
                        <CategoryIcon className="h-5 w-5 text-white" />
                      </div>
                      {category.category}
                      <Badge variant="outline">{category.guides.length} guides</Badge>
                    </CardTitle>
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-200" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-200" />
                    )}
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {category.guides.map(guide => (
                        <div key={guide.id}>
                          <div
                            className={cn(
                              "p-4 rounded-lg border cursor-pointer transition-colors",
                              selectedGuide?.id === guide.id
                                ? "bg-mutagim-red/10 border-mutagim-red"
                                : "bg-gray-50 hover:bg-gray-100 border-transparent"
                            )}
                            onClick={() => selectGuide(guide)}
                            data-testid={`guide-${guide.id}`}
                          >
                            <h4 className="font-medium">{guide.title}</h4>
                            <p className="text-sm text-gray-500 mt-1">
                              {guide.description}
                            </p>
                          </div>

                          {selectedGuide?.id === guide.id && (
                            <div className="mt-2 p-6 bg-white border rounded-lg prose prose-sm max-w-none">
                              <div 
                                dangerouslySetInnerHTML={{ 
                                  __html: guide.content
                                    .replace(/\n/g, '<br>')
                                    .replace(/##\s(.+)/g, '<h3 class="text-lg font-bold mt-4 mb-2">$1</h3>')
                                    .replace(/###\s(.+)/g, '<h4 class="font-semibold mt-3 mb-1">$1</h4>')
                                    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                                    .replace(/`(.+?)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>')
                                    .replace(/- (.+)/g, '<li class="ml-4">$1</li>')
                                    .replace(/💡/g, '<span class="text-yellow-500">💡</span>')
                                    .replace(/⚠️/g, '<span class="text-orange-500">⚠️</span>')
                                    .replace(/✅/g, '<span class="text-green-500">✅</span>')
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredGuides.length === 0 && (
          <div className="text-center py-12">
            <HelpCircle className="h-12 w-12 text-white mx-auto mb-4" />
            <p className="text-gray-500">Aucun guide trouvé pour "{searchTerm}"</p>
            <Button
              variant="link"
              onClick={() => setSearchTerm('')}
              className="mt-2"
            >
              Effacer la recherche
            </Button>
          </div>
        )}

        {/* Footer Help */}
        <div className="mt-12 p-6 bg-gradient-to-r from-mutagim-dark to-gray-800 rounded-xl text-white text-center">
          <h3 className="text-lg font-bold mb-2">Besoin d'aide supplémentaire ?</h3>
          <p className="text-white mb-4">
            Notre équipe support est disponible pour répondre à vos questions.
          </p>
          <div className="flex items-center justify-center gap-4">
            <a
              href="mailto:support@mutagim.shop"
              className="inline-flex items-center gap-2 bg-white text-mutagim-dark px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
            >
              <Mail className="h-4 w-4" />
              Email
            </a>
            <a
              href="#"
              className="inline-flex items-center gap-2 border border-white/30 px-4 py-2 rounded-lg font-medium hover:bg-white/10 transition-colors"
            >
              <Video className="h-4 w-4" />
              Tutoriels vidéo
            </a>
          </div>
        </div>
      </main>
      </div>
    </div>
  );
}

export default AdminHelpPage;
